import { useEffect } from 'react'

interface HotkeyOptions {
  meta?: boolean
  shift?: boolean
  /** Fire even while a text field has focus. Off by default. */
  allowInInput?: boolean
}

/**
 * Register a global keyboard shortcut.
 *
 * Guards against firing while the user is typing — a bare "k" shortcut that
 * also triggers inside a comment box is worse than no shortcut. `meta: true`
 * matches Cmd on macOS and Ctrl elsewhere, because a shortcut hard-coded to
 * one of them is broken for half the users.
 */
export function useHotkey(key: string, handler: () => void, options: HotkeyOptions = {}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== key.toLowerCase()) return
      if (options.meta && !(event.metaKey || event.ctrlKey)) return
      if (!options.meta && (event.metaKey || event.ctrlKey)) return
      if (options.shift && !event.shiftKey) return
      if (!options.allowInInput) {
        const target = event.target as HTMLElement | null
        const tag = target?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
      }
      event.preventDefault()
      handler()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [key, handler, options.meta, options.shift, options.allowInInput])
}
